import { CHANNELS } from "../../shared/channels";
import { FEATURES, Feature, Level, OWNER_ONLY } from "../../shared/access";
import { PeopleBoard, PersonView } from "../../shared/types";
import { call } from "../bridge";
import { byId } from "../dom";
import { onViewShown } from "../navigation";
import { t } from "../i18n";
import { buzz, flash } from "../tweens";
import { setNeedle } from "./today_view";

// fractal's dashboard: who can sign in, what each of them sees, and what their chi chat costs on fractal's key.
// every switch saves straight away, the server checks every call against what's saved here

let board: PeopleBoard | null = null;
const open = new Set<string>();

const LEVEL_NAMES: Record<Level, string> = { none: "off", read: "view", edit: "edit" };

function levelName(level: Level): string {
  return t(LEVEL_NAMES[level]);
}

function dollars(usd: number): string {
  return usd < 10 ? `$${usd.toFixed(2)}` : `$${usd.toFixed(0)}`;
}

function setStatus(text: string): void {
  const status = byId("people-status");
  status.textContent = text;
  flash(status);
}

function errorOf(error: unknown): string {
  return String(error instanceof Error ? error.message : error).replace(/^.*Error: /, "");
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function sharedCount(person: PersonView): number {
  return FEATURES.filter((feature) => (person.access[feature.id] || "none") !== "none").length;
}

function summaryLine(person: PersonView): string {
  const vars = { on: sharedCount(person), total: FEATURES.length, spent: dollars(person.spentUsd), cap: dollars(person.chatCapUsd) };
  return person.chatCapUsd ? t("{on} of {total} on · chi {spent} of {cap}", vars) : t("{on} of {total} on · chi {spent}", vars);
}

async function setLevel(person: PersonView, feature: Feature, level: Level, card: HTMLElement): Promise<void> {
  buzz();
  person.access[feature.id] = level;
  card.querySelector<HTMLElement>(".person-meta")!.textContent = summaryLine(person);
  try {
    board = await call<PeopleBoard>(CHANNELS.peopleSave, person.id, { access: { [feature.id]: level } });
    const name = person.name;
    const label = t(feature.label);
    if (level === "none") setStatus(t("{feature} off for {name}", { feature: label, name }));
    else setStatus(level === "read" ? t("{name} can view {feature}", { name, feature: label.toLowerCase() }) : t("{name} can edit {feature}", { name, feature: label.toLowerCase() }));
  } catch (error) {
    setStatus(errorOf(error));
    await reload();
  }
}

// two level features are a switch, refs and ocs get off, view and edit
function control(person: PersonView, feature: Feature, card: HTMLElement): HTMLElement {
  const level = person.access[feature.id] || "none";
  if (feature.levels.length === 2) {
    const label = el("label", "toggle");
    label.setAttribute("aria-label", t("{feature} for {name}", { feature: t(feature.label), name: person.name }));
    const box = el("input");
    box.type = "checkbox";
    box.checked = level !== "none";
    box.addEventListener("change", () => void setLevel(person, feature, box.checked ? feature.levels[1] : "none", card));
    const track = el("span", "toggle-track");
    track.append(el("i"));
    label.append(box, track);
    return label;
  }
  const seg = el("div", "seg");
  seg.setAttribute("role", "radiogroup");
  seg.setAttribute("aria-label", t("{feature} for {name}", { feature: t(feature.label), name: person.name }));
  for (const option of feature.levels) {
    const button = el("button", "seg-option", levelName(option));
    button.type = "button";
    button.setAttribute("role", "radio");
    button.setAttribute("aria-checked", String(option === level));
    button.classList.toggle("active", option === level);
    button.addEventListener("click", () => {
      if (button.classList.contains("active")) return;
      seg.querySelectorAll<HTMLElement>(".seg-option").forEach((other) => {
        other.classList.toggle("active", other === button);
        other.setAttribute("aria-checked", String(other === button));
      });
      void setLevel(person, feature, option, card);
    });
    seg.append(button);
  }
  return seg;
}

function featureRow(person: PersonView, feature: Feature, card: HTMLElement): HTMLElement {
  const row = el("div", "perm-row");
  const text = el("div", "perm-text");
  text.append(el("strong", "", t(feature.label)), el("small", "", t(feature.detail)));
  row.append(text, control(person, feature, card));
  return row;
}

function capRow(person: PersonView): HTMLElement {
  const wrap = el("div", "person-cap");
  const used = person.chatCapUsd ? Math.min(1, person.spentUsd / person.chatCapUsd) : 0;
  const track = el("div", "led-track budget");
  track.hidden = !person.chatCapUsd;
  track.classList.toggle("over", used >= 1);
  const fill = el("i");
  fill.style.width = `calc(${(used * 100).toFixed(1)}% - 2px)`;
  track.append(fill);
  const line = el("div", "person-cap-line");
  const field = el("label", "field", t("monthly chi cap, usd"));
  const input = el("input");
  input.inputMode = "decimal";
  input.value = String(person.chatCapUsd || 0);
  input.addEventListener("change", async () => {
    const value = Math.max(0, Number(input.value.replace(",", ".")) || 0);
    try {
      board = await call<PeopleBoard>(CHANNELS.peopleSave, person.id, { chatCapUsd: value });
      setStatus(value ? t("{name}'s chi stops at {amount} a month", { name: person.name, amount: dollars(value) }) : t("no cap for {name}", { name: person.name }));
      render();
    } catch (error) {
      setStatus(errorOf(error));
    }
  });
  field.append(input);
  const spent = el("span", "hint", t("{spent} over {count} answers this month. 0 means no cap", { spent: dollars(person.spentUsd), count: person.requests }));
  line.append(field);
  wrap.append(track, line, spent);
  return wrap;
}

function personCard(person: PersonView): HTMLElement {
  const card = el("details", "panel person");
  card.open = open.has(person.id);
  card.addEventListener("toggle", () => {
    if (card.open) open.add(person.id);
    else open.delete(person.id);
  });
  const summary = el("summary", "person-head");
  const name = el("span", "person-name", person.name);
  const meta = el("span", "person-meta", summaryLine(person));
  const id = el("span", "person-id", person.id);
  summary.append(name, meta, id);

  const body = el("div", "person-body");
  const rows = el("div", "perm-list");
  rows.append(...FEATURES.map((feature) => featureRow(person, feature, card)));
  const remove = el("button", "pill small danger", t("remove {name}", { name: person.name }));
  remove.type = "button";
  remove.addEventListener("click", async () => {
    if (!confirm(t("remove {name}? they get signed out. their own notes and chats stay on the server if you add them back", { name: person.name }))) return;
    try {
      board = await call<PeopleBoard>(CHANNELS.peopleRemove, person.id);
      setStatus(t("{name} removed", { name: person.name }));
      render();
    } catch (error) {
      setStatus(errorOf(error));
    }
  });
  body.append(capRow(person), rows, remove);
  card.append(summary, body);
  return card;
}

function render(): void {
  if (!board) return;
  const others = board.people.reduce((sum, person) => sum + person.spentUsd, 0);
  byId("people-count").textContent = String(board.people.length);
  byId("people-spend").textContent = dollars(others);
  byId("people-mine").textContent = dollars(board.ownerSpentUsd);
  setNeedle("people-count", board.people.length / 8);
  setNeedle("people-spend", others / Math.max(1, others + board.ownerSpentUsd));
  setNeedle("people-mine", board.ownerSpentUsd / Math.max(1, others + board.ownerSpentUsd));
  const list = byId("people-list");
  if (!board.people.length) {
    list.replaceChildren(el("p", "empty", t("just you for now")));
    return;
  }
  list.replaceChildren(...board.people.map(personCard));
}

async function reload(): Promise<void> {
  try {
    board = await call<PeopleBoard>(CHANNELS.peopleList);
    render();
  } catch (error) {
    setStatus(errorOf(error));
  }
}

async function addPerson(event: Event): Promise<void> {
  event.preventDefault();
  const name = byId<HTMLInputElement>("people-add-name");
  const id = byId<HTMLInputElement>("people-add-id");
  try {
    board = await call<PeopleBoard>(CHANNELS.peopleAdd, id.value.trim(), name.value.trim());
    const added = board.people.find((person) => person.id === id.value.trim());
    if (added) open.add(added.id);
    setStatus(t("{name} can sign in now. they start with chi, notes, pings, routines and goals", { name: name.value.trim() }));
    name.value = "";
    id.value = "";
    render();
  } catch (error) {
    setStatus(errorOf(error));
  }
}

export function setupPeopleView(): void {
  byId("people-refresh").addEventListener("click", () => void reload());
  byId("people-add").addEventListener("submit", (event) => void addPerson(event));
  byId("people-owner-only").replaceChildren(...OWNER_ONLY.map((item) => {
    const row = el("li");
    row.append(el("strong", "", t(item.label)), el("small", "", t(item.detail)));
    return row;
  }));
  onViewShown("people", () => void reload());
}
